import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { COLOR_TOKENS } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { TYPOGRAPHY_VARIANTS } from '@backrs_ui/common-ui/src/designSystem/typography/typography';
import { formatCurrency } from '@backrs_ui/common-ui/src/utils/stringFormatters.util';
import { Box, ClickAwayListener, Typography, Tooltip as MUITooltip, } from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Line } from 'react-chartjs-2';
import { ROILineChartHeader } from './ROILineChartHeader';
import { AppIcon } from '../..';
var ROI_LINE_CHART_STRINGS = appStrings.modules.charts.returnOnInvestment;
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, annotationPlugin);
export var ROILineChart = function (_a) {
    var averageData = _a.averageData, actualData = _a.actualData, title = _a.title, total_roi = _a.total_roi, average_roi = _a.average_roi;
    var _b = useState(false), isLegendToolTipOpen = _b[0], setIsLegendToolTipOpen = _b[1];
    var years = actualData.map(function (_, index) { return index; });
    var data = {
        labels: years,
        datasets: [
            {
                label: ROI_LINE_CHART_STRINGS.average,
                data: averageData,
                borderColor: COLOR_TOKENS['Brand/400'],
                borderDash: [5, 5],
                tension: 0.3,
                pointRadius: 0,
                pointHoverRadius: 0,
                hoverBorderWidth: 0,
            },
            {
                label: title,
                data: actualData,
                borderColor: COLOR_TOKENS['Brand/500'],
                tension: 0.3,
                pointRadius: 0,
                pointHoverRadius: 0,
                hoverBorderWidth: 0,
            },
        ],
    };
    var lowestTick = Math.min.apply(Math, actualData);
    var highestTick = Math.max.apply(Math, actualData);
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: {
                title: {
                    display: true,
                    text: ROI_LINE_CHART_STRINGS.yAxisLabel,
                    color: COLOR_TOKENS['Colors/Text/text-tertiary'],
                    font: {
                        size: 12,
                        family: 'Sarabun, sans-serif',
                    },
                },
                grid: {
                    tickColor: 'transparent',
                    display: true,
                    color: function (context) {
                        var value = context.tick.value;
                        if (value === 0) {
                            return COLOR_TOKENS['Colors/Foreground/fg-tertiary'];
                        }
                        return COLOR_TOKENS['Colors/Border/border-tertiary'];
                    },
                },
                border: {
                    display: false,
                },
                min: Math.min(lowestTick - 50000, -250000),
                max: Math.max(highestTick + 50000, 1000000),
                afterBuildTicks: function (axis) {
                    var minTick = Math.min(Math.floor(lowestTick / 250000) * 250000, -250000);
                    var maxTick = Math.max(Math.ceil(highestTick / 250000) * 250000, 1000000);
                    var tickCount = (maxTick - minTick) / 250000 + 1;
                    axis.ticks = Array.from({ length: tickCount }, function (_, i) { return ({
                        value: minTick + i * 250000,
                    }); });
                },
                ticks: {
                    callback: function (value) {
                        var valueAsNumber = Number(value);
                        if (valueAsNumber % 250000 === 0) {
                            var absValue = Math.abs(valueAsNumber);
                            var suffix = absValue >= 1000000 ? 'M' : 'K';
                            var formattedValue = absValue >= 1000000 ? absValue / 1000000 : absValue / 1000;
                            return "".concat(valueAsNumber < 0 ? '-' : '', "$").concat(formattedValue).concat(suffix, " ");
                        }
                        return '';
                    },
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                },
            },
            x: {
                title: {
                    display: true,
                    text: ROI_LINE_CHART_STRINGS.xAxisLabel,
                    color: COLOR_TOKENS['Colors/Text/text-tertiary'],
                    padding: {
                        top: 16,
                        bottom: 0,
                    },
                    font: {
                        size: 12,
                        family: 'Sarabun, sans-serif',
                    },
                },
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    align: 'end',
                    font: {
                        size: 12,
                        weight: 'lighter',
                        family: 'Sarabun, sans-serif',
                    },
                    callback: function (_, index) {
                        if (index >= 2 && index % 2 === 0) {
                            return index;
                        }
                        return '';
                    },
                },
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            tooltip: {
                enabled: true,
                displayColors: false,
                padding: {
                    top: 8,
                    bottom: 8,
                    left: 12,
                    right: 12,
                },
                caretSize: 0,
                yAlign: 'bottom',
                callbacks: {
                    title: function (context) {
                        return "".concat(ROI_LINE_CHART_STRINGS.xAxisLabel, " ").concat(context[0].label);
                    },
                    label: function (context) {
                        var label = context.dataset.label || '';
                        var value = context.parsed.y;
                        return "".concat(label, ": ").concat(formatCurrency(value));
                    },
                },
            },
            legend: {
                labels: {
                    generateLabels: function () { return []; },
                },
            },
            title: {
                display: false,
            },
        },
        hover: {
            mode: 'index',
            intersect: false,
        },
    };
    var LegendItem = function (_a) {
        var title = _a.title, color = _a.color, isDashed = _a.isDashed, tooltip = _a.tooltip;
        return (_jsxs(Box, { sx: legendItemSx, children: [_jsx(Box, { sx: legendItemColorSx(color) }), isDashed && _jsx(Box, { sx: legendItemColorSx(color) }), _jsx(Typography, { variant: "text-sm-regular", color: function (_a) {
                        var backrs = _a.backrs;
                        return backrs.colors['Colors/Text/text-tertiary'];
                    }, children: title }), tooltip && (_jsx(ClickAwayListener, { onClickAway: function () { return setIsLegendToolTipOpen(false); }, children: _jsx(MUITooltip, { open: isLegendToolTipOpen, onClose: function () { return setIsLegendToolTipOpen(false); }, placement: "top", arrow: true, disableFocusListener: true, disableHoverListener: true, disableTouchListener: true, title: ROI_LINE_CHART_STRINGS.tooltip, sx: tooltipSx, children: _jsx(Box, { sx: tooltipIconSx, onClick: function () { return setIsLegendToolTipOpen(function (prev) { return !prev; }); }, children: _jsx(AppIcon, { iconName: "InfoCircle", diameter: "16px", color: "Colors/Foreground/fg-quinary" }) }) }) }))] }));
    };
    return (_jsxs(Box, { sx: containerSx, children: [_jsx(ROILineChartHeader, { total_roi: total_roi, average_roi: average_roi }), _jsxs(Box, { sx: legendSx, children: [_jsx(LegendItem, { title: title, color: COLOR_TOKENS['Brand/500'], isDashed: false }), _jsx(LegendItem, { title: ROI_LINE_CHART_STRINGS.rankedAverageLegend, color: COLOR_TOKENS['Brand/400'], isDashed: true, tooltip: ROI_LINE_CHART_STRINGS.rankedAverageTooltip })] }), _jsx(Box, { sx: chartContainerSx, children: _jsx(Line, { data: data, options: options }) })] }));
};
var chartContainerSx = {
    width: '100%',
    height: '100%',
};
var containerSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        flexDirection: 'column',
        '@media (max-width: 768px)': {
            width: '100%',
        },
        '@media (max-width: 400px)': {
            width: '100%',
        },
        height: { xs: 'auto', lg: '405px' },
        backgroundColor: backrs.colors['Colors/Base/White'],
        border: "1px solid ".concat(backrs.colors['Colors/Foreground/fg-primary']),
        boxShadow: backrs.shadows['shadow-xs'],
        borderRadius: backrs.radius['radius-xl'],
        padding: {
            xs: backrs.spacing['spacing-xl'],
            sm: backrs.spacing['spacing-2xl'],
        },
    });
};
var legendSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        alignSelf: { xs: 'flex-end', sm: 'flex-end' },
        display: 'flex',
        alignItems: { xs: 'flex-end', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: backrs.spacing['spacing-lg'],
        fontSize: TYPOGRAPHY_VARIANTS['text-sm-regular'].fontSize,
        mb: { xs: backrs.spacing['spacing-2xl'], sm: backrs.spacing['spacing-none'] },
    });
};
var legendItemSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-xs'],
    });
};
var legendItemColorSx = function (color) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            width: '6px',
            height: '4px',
            borderRadius: backrs.radius['radius-full'],
            backgroundColor: color,
            alignSelf: 'center',
            justifySelf: 'center',
            marginTop: '2px',
        });
    };
};
var tooltipIconSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        marginLeft: backrs.spacing['spacing-xxs'],
    });
};
var tooltipSx = function (_a) {
    var backrs = _a.backrs;
    return ({
        marginLeft: backrs.spacing['spacing-xs'],
    });
};
